import { AlertCircle, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { type MouseEvent, useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import { useLoan } from '@/hooks/use-loans';
import {
  useApplyRestructure,
  useApproveRestructure,
  useCreateRestructure,
  useRejectRestructure,
  useRestructures,
  useSubmitRestructure,
} from '@/hooks/use-restructures';
import { cn } from '@/lib/cn';
import { fmtCurrency, fmtDate, fmtPercent } from '@/lib/format';
import { hasRoleAccess } from '@/lib/role-access';
import { useAuthStore } from '@/stores/auth-store';
import type { LoanRestructure, RestructureType } from '@/types';
import { RESTRUCTURE_STATUS_CONFIG, RESTRUCTURE_TYPE_LABELS } from '@/types';

interface Props {
  loanId: string;
}

export function RestructureTab({ loanId }: Props) {
  const user = useAuthStore(s => s.user);
  const { data: _loan } = useLoan(loanId);
  const { data: restructures, isLoading } = useRestructures(loanId);
  const create = useCreateRestructure(loanId);
  const submit = useSubmitRestructure(loanId);
  const approve = useApproveRestructure(loanId);
  const reject = useRejectRestructure(loanId);
  const apply = useApplyRestructure(loanId);

  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showRejectReason, setShowRejectReason] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [formErrors, setFormErrors] = useState<{
    proposedPrincipal?: string;
    proposedRate?: string;
    proposedTermMonths?: string;
  }>({});

  const [formData, setFormData] = useState<Partial<LoanRestructure>>({
    type: 'RATE_CHANGE',
  });

  const canApproveRestructure = hasRoleAccess(user?.role, ['ADMIN', 'MANAGER']);

  function sanitizeDecimalInput(value: string) {
    return value.replace(/[^0-9.]/g, '');
  }

  function validateDecimal(value: string | undefined, min: number, required = false) {
    const raw = value ?? '';
    if (!raw.trim()) {
      return required ? 'This field is required.' : '';
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 'Enter a valid number.';
    if (parsed < min) return `Value must be at least ${min}.`;
    return '';
  }

  function validateInteger(value: string | undefined, min: number, required = false) {
    const raw = value ?? '';
    if (!raw.trim()) {
      return required ? 'This field is required.' : '';
    }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return 'Enter a valid whole number.';
    if (parsed < min) return `Value must be at least ${min}.`;
    return '';
  }

  async function handleCreate() {
    const nextErrors = {
      proposedPrincipal: validateDecimal(formData.proposedPrincipal, 0),
      proposedRate: validateDecimal(formData.proposedRate, 0),
      proposedTermMonths:
        formData.type === 'TERM_EXTENSION'
          ? validateInteger(String(formData.proposedTermMonths ?? ''), 1, true)
          : '',
    };
    setFormErrors(nextErrors);

    if (!formData.type) return;
    if (Object.values(nextErrors).some(Boolean)) return;
    await create.mutateAsync(formData);
    setShowModal(false);
    setFormData({ type: 'RATE_CHANGE' });
    setFormErrors({});
  }

  async function handleSubmit(id: string) {
    await submit.mutateAsync(id);
  }

  async function handleApprove(id: string) {
    await approve.mutateAsync(id);
  }

  async function handleReject(id: string) {
    if (!rejectReason.trim()) return;
    await reject.mutateAsync({ id, reason: rejectReason });
    setShowRejectReason(null);
    setRejectReason('');
  }

  async function handleApply(id: string) {
    await apply.mutateAsync(id);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading restructures…
      </div>
    );
  }

  const items = restructures ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Restructure History</h3>
        <Button
          variant="primary"
          onClick={() => setShowModal(true)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Restructure
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <AlertCircle className="w-10 h-10 mb-3" />
          <p className="text-sm">No restructures recorded</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const cfg = RESTRUCTURE_STATUS_CONFIG[item.status];
            const isExpanded = expandedId === item.id;
            return (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
              >
                <div
                  className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900">
                          #{item.restructureNumber} - {RESTRUCTURE_TYPE_LABELS[item.type]}
                        </span>
                        <span
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{ color: cfg.color, backgroundColor: cfg.bgColor }}
                        >
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{fmtDate(item.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.status === 'DRAFT' && (
                        <Button
                          variant="secondary"
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleSubmit(item.id);
                          }}
                          disabled={submit.isPending}
                          className="px-3 py-1.5 text-xs font-medium border border-blue-500 bg-indigo-50 text-blue-700 rounded-md hover: transition-colors disabled:opacity-50"
                        >
                          Submit
                        </Button>
                      )}
                      {item.status === 'PENDING_APPROVAL' && canApproveRestructure && (
                        <>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleApprove(item.id);
                            }}
                            disabled={approve.isPending}
                            className="px-3 py-1.5 text-xs font-medium border border-green-300 bg-green-50 text-green-700
                                     rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setShowRejectReason(item.id);
                            }}
                            className="px-3 py-1.5 text-xs font-medium border border-red-300 bg-red-50 text-red-700
                                     rounded-md hover:bg-red-100 transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {item.status === 'APPROVED' && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleApply(item.id);
                          }}
                          disabled={apply.isPending}
                          className="px-3 py-1.5 text-xs font-medium border border-blue-300 bg-blue-50 text-blue-700
                                   rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                        >
                          Apply
                        </button>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-200 space-y-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider">
                          Original Terms
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="text-gray-500">Principal</p>
                            <p className="text-gray-900 font-medium">
                              {fmtCurrency(item.originalPrincipal)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Interest Rate</p>
                            <p className="text-gray-900 font-medium">
                              {fmtPercent(item.originalRate)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Term</p>
                            <p className="text-gray-900 font-medium">
                              {item.originalTermMonths} months
                            </p>
                          </div>
                          {item.originalMaturityDate && (
                            <div>
                              <p className="text-gray-500">Maturity</p>
                              <p className="text-gray-900 font-medium">
                                {fmtDate(item.originalMaturityDate)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {(item.proposedPrincipal || item.proposedRate || item.proposedTermMonths) && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider">
                            Proposed Terms
                          </h4>
                          <div className="space-y-2 text-sm">
                            {item.proposedPrincipal && (
                              <div>
                                <p className="text-gray-500">Principal</p>
                                <p className="text-gray-900 font-medium">
                                  {fmtCurrency(item.proposedPrincipal)}
                                </p>
                              </div>
                            )}
                            {item.proposedRate && (
                              <div>
                                <p className="text-gray-500">Interest Rate</p>
                                <p className="text-gray-900 font-medium">
                                  {fmtPercent(item.proposedRate)}
                                </p>
                              </div>
                            )}
                            {item.proposedTermMonths && (
                              <div>
                                <p className="text-gray-500">Term</p>
                                <p className="text-gray-900 font-medium">
                                  {item.proposedTermMonths} months
                                </p>
                              </div>
                            )}
                            {item.proposedMaturityDate && (
                              <div>
                                <p className="text-gray-500">Maturity</p>
                                <p className="text-gray-900 font-medium">
                                  {fmtDate(item.proposedMaturityDate)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {item.reason && (
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Reason</p>
                        <p className="text-sm text-gray-600">{item.reason}</p>
                      </div>
                    )}

                    {item.internalNotes && (
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Internal Notes</p>
                        <p className="text-sm text-gray-600">{item.internalNotes}</p>
                      </div>
                    )}

                    {item.approvedBy && (
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Approved By</p>
                        <p className="text-sm text-gray-600">
                          {item.approvedBy.firstName} {item.approvedBy.lastName} on{' '}
                          {fmtDate(item.approvedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {showRejectReason === item.id && (
                  <div className="p-4 bg-red-50 border-t border-red-200 space-y-3">
                    <textarea
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Enter rejection reason…"
                      className="w-full px-3 py-2 border border-red-300 rounded-md text-sm font-medium
                               bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2
                               focus:ring-red-500 focus:border-transparent"
                      rows={3}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReject(item.id)}
                        disabled={!rejectReason.trim() || reject.isPending}
                        className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md
                                 hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        Confirm Rejection
                      </button>
                      <button
                        onClick={() => {
                          setShowRejectReason(null);
                          setRejectReason('');
                        }}
                        className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-md
                                 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ModalContentWrapper
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setFormData({ type: 'RATE_CHANGE' });
            setFormErrors({});
          }}
          title="Create New Restructure"
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Restructure Type</label>
              <select
                value={formData.type || ''}
                onChange={e =>
                  setFormData({ ...formData, type: e.target.value as RestructureType })
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {(
                  [
                    'RATE_CHANGE',
                    'TERM_EXTENSION',
                    'LIMIT_INCREASE',
                    'LIMIT_DECREASE',
                    'HARDSHIP_VARIATION',
                    'FULL_RESTRUCTURE',
                    'PAYMENT_HOLIDAY',
                  ] as RestructureType[]
                ).map(type => (
                  <option key={type} value={type}>
                    {RESTRUCTURE_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Proposed Principal</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.proposedPrincipal ?? ''}
                  onChange={e => {
                    setFormData({
                      ...formData,
                      proposedPrincipal: sanitizeDecimalInput(e.target.value),
                    });
                    setFormErrors(prev => ({ ...prev, proposedPrincipal: undefined }));
                  }}
                  onBlur={() =>
                    setFormErrors(prev => ({
                      ...prev,
                      proposedPrincipal: validateDecimal(formData.proposedPrincipal as string, 0),
                    }))
                  }
                  placeholder="Leave blank to keep current"
                  className={cn(
                    'mt-1 w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.proposedPrincipal ? 'border-red-300 bg-red-50' : 'border-gray-300',
                  )}
                />
                {formErrors.proposedPrincipal && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.proposedPrincipal}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Proposed Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.proposedRate ?? ''}
                  onChange={e => {
                    setFormData({
                      ...formData,
                      proposedRate: sanitizeDecimalInput(e.target.value),
                    });
                    setFormErrors(prev => ({ ...prev, proposedRate: undefined }));
                  }}
                  onBlur={() =>
                    setFormErrors(prev => ({
                      ...prev,
                      proposedRate: validateDecimal(formData.proposedRate as string, 0),
                    }))
                  }
                  placeholder="Leave blank to keep current"
                  className={cn(
                    'mt-1 w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.proposedRate ? 'border-red-300 bg-red-50' : 'border-gray-300',
                  )}
                />
                {formErrors.proposedRate && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.proposedRate}</p>
                )}
              </div>
            </div>

            {formData.type === 'TERM_EXTENSION' && (
              <div>
                <label className="text-sm font-medium text-gray-700">Proposed Term (months)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.proposedTermMonths ?? ''}
                  onChange={e => {
                    const sanitized = e.target.value.replace(/[^\d]/g, '');
                    setFormData({
                      ...formData,
                      proposedTermMonths: sanitized ? Number.parseInt(sanitized, 10) : undefined,
                    });
                    setFormErrors(prev => ({ ...prev, proposedTermMonths: undefined }));
                  }}
                  onBlur={() =>
                    setFormErrors(prev => ({
                      ...prev,
                      proposedTermMonths: validateInteger(
                        String(formData.proposedTermMonths ?? ''),
                        1,
                        true,
                      ),
                    }))
                  }
                  className={cn(
                    'mt-1 w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.proposedTermMonths ? 'border-red-300 bg-red-50' : 'border-gray-300',
                  )}
                />
                {formErrors.proposedTermMonths && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.proposedTermMonths}</p>
                )}
              </div>
            )}

            {formData.type === 'HARDSHIP_VARIATION' && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700">Hardship Category</label>
                  <select
                    value={formData.hardshipCategory || ''}
                    onChange={e => setFormData({ ...formData, hardshipCategory: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category…</option>
                    <option value="FINANCIAL_HARDSHIP">Financial Hardship</option>
                    <option value="UNEMPLOYMENT">Unemployment</option>
                    <option value="ILLNESS">Illness</option>
                    <option value="FAMILY_BREAKDOWN">Family Breakdown</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Hardship Details</label>
                  <textarea
                    value={formData.hardshipDetails || ''}
                    onChange={e => setFormData({ ...formData, hardshipDetails: e.target.value })}
                    placeholder="Describe the hardship circumstances…"
                    rows={3}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Reason for Restructure</label>
              <textarea
                value={formData.reason || ''}
                onChange={e => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Explain the business reason for this restructure…"
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Internal Notes</label>
              <textarea
                value={formData.internalNotes || ''}
                onChange={e => setFormData({ ...formData, internalNotes: e.target.value })}
                placeholder="Internal notes (not visible to borrower)…"
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              onClick={() => {
                setShowModal(false);
                setFormData({ type: 'RATE_CHANGE' });
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium
                         hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={create.isPending || !formData.type}
              className="w-fit px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors disabled:opacity-50"
            >
              {create.isPending ? 'Creating…' : 'Create Restructure'}
            </Button>
          </div>
        </ModalContentWrapper>
      )}
    </div>
  );
}
